import PropTypes from 'prop-types'
import clsx from 'clsx'
import { NavLink } from 'react-router-dom'
import {
  ListItemText,
  ListItemIcon,
  ListItem,
  IconButton,
  List,
  Drawer,
  Collapse,
  Box,
  Link,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useUserContext } from '../../../auth/UserProvider'

import * as routes from '../../../routes'
import useTranslations from '../../../i18n/useTranslations'
import Logo from '../../../assets/images/ctt-logo.svg'
import LogoIcon from '../../../assets/images/ctt-logo-icon.svg'

import { ReactComponent as CaretDoubleLeft } from '../../../assets/icons/caret-double-left.svg'
import { ReactComponent as CaretDoubleRight } from '../../../assets/icons/caret-double-right.svg'
import { ReactComponent as CaretDown } from '../../../assets/icons/caret-down.svg'
import { ReactComponent as CaretTop } from '../../../assets/icons/caret-up.svg'
import React, { useState, Fragment } from 'react'

import useStyles from './styles'

const SidebarMenu = ({ menuItems, isOpen, onToggleMenu }) => {
  const classes = useStyles()
  const t = useTranslations()
  const user = useUserContext()
  const theme = useTheme()
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'))

  const [openSubmenus, setOpenSubmenus] = useState(
    Object.fromEntries(menuItems.map(item => [item.title, true])),
  )

  const handleSubmenuToggle = title => {
    setOpenSubmenus(prevOpenSubmenus => ({
      ...prevOpenSubmenus,
      [title]: !prevOpenSubmenus[title],
    }))
  }

  const handleMouseEnter = title => {
    setOpenSubmenus(prevOpenSubmenus => ({
      ...prevOpenSubmenus,
      [title]: true,
    }))
  }

  const filteredMenuItems = menuItems.filter(item => {
    if (item.requiredPermission && !user[item.requiredPermission]) {
      return false
    }
    if (
      item.allowedUserCodes &&
      !item.allowedUserCodes.includes(user.clientCode)
    ) {
      return false
    }
    if (
      item.requiredEnvironments &&
      !item.requiredEnvironments.includes(process.env.REACT_APP_ENVIRONMENT)
    ) {
      return false
    }
    return true
  })

  return (
    <Drawer
      variant="permanent"
      className={clsx(classes.drawer, { [classes.drawerOpen]: isOpen })}
      classes={{ paper: clsx(classes.paper, { [classes.drawerOpen]: isOpen }) }}
    >
      {isSmDown ? null : (
        <Box className={classes.logoContainer}>
          <Link underline="none" component={NavLink} to={routes.HOME.linkTo()}>
            <img
              src={isOpen ? Logo : LogoIcon}
              alt=""
              className={classes.logo}
            />
          </Link>
          <IconButton
            onClick={onToggleMenu}
            className={classes.toggleButton}
            size="large"
          >
            {isOpen ? <CaretDoubleLeft /> : <CaretDoubleRight />}
          </IconButton>
        </Box>
      )}
      <List component="nav" className={classes.nav}>
        {filteredMenuItems.map(
          ({ title, Icon, path, extraClass, elements }) => (
            <Fragment key={title}>
              <Tooltip
                placement="right"
                title={<Typography variant="body2">{t(title)}</Typography>}
                disableHoverListener={isOpen}
                disableFocusListener={isOpen}
                disableTouchListener={isOpen}
              >
                <ListItem
                  button
                  component={elements ? 'div' : NavLink}
                  exact
                  to={path}
                  className={clsx(classes.navItem, classes[extraClass])}
                  activeClassName="Mui-selected"
                  onClick={
                    elements ? () => handleSubmenuToggle(title) : undefined
                  }
                  onMouseEnter={
                    elements ? () => handleMouseEnter(title) : undefined
                  }
                >
                  <ListItemIcon className={classes.navIcon}>
                    <Icon size={24} />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.navText}
                    primary={t(title)}
                  />
                  {elements ? (
                    openSubmenus[title] ? (
                      <CaretTop className={classes.arrowIcon} />
                    ) : (
                      <CaretDown className={classes.arrowIcon} />
                    )
                  ) : null}
                </ListItem>
              </Tooltip>
              {elements && (
                <Collapse in={openSubmenus[title]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {elements.map(subItem =>
                      !subItem?.allowedUserCodes ||
                      (subItem?.allowedUserCodes &&
                        subItem?.allowedUserCodes?.includes(
                          user.clientCode,
                        )) ? (
                        <ListItem
                          key={subItem.title}
                          button
                          component={NavLink}
                          exact
                          to={subItem.path}
                          className={clsx(classes.navItem, classes.subNavItem)}
                          activeClassName="Mui-selected"
                        >
                          <ListItemIcon className={classes.navIcon}>
                            <subItem.Icon size={24} />
                          </ListItemIcon>
                          <ListItemText
                            className={classes.navText}
                            primary={t(subItem.title)}
                          />
                        </ListItem>
                      ) : null,
                    )}
                  </List>
                </Collapse>
              )}
            </Fragment>
          ),
        )}
      </List>
    </Drawer>
  )
}

SidebarMenu.propTypes = {
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      Icon: PropTypes.elementType,
      path: PropTypes.string,
      elements: PropTypes.arrayOf(
        PropTypes.shape({
          title: PropTypes.string,
          Icon: PropTypes.elementType,
          path: PropTypes.string,
        }),
      ),
    }),
  ),
  isOpen: PropTypes.bool,
  onToggleMenu: PropTypes.func,
}

export default SidebarMenu
